import { takeLatest, all, call, put, select } from 'redux-saga/effects';
import { getFirebase } from 'react-redux-firebase';
import AlgoliaClient from 'utils/algoliaService';
import ContentfulClient from 'utils/contentfulService';
import Mixpanel from 'utils/mixpanelService';
import Config from 'utils/getEnvConfig';
import { getCookie } from 'utils/cookieStore';
import _isEmpty from 'lodash/isEmpty';
import _clone from 'lodash/clone';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _flatten from 'lodash/flatten';
import format from 'date-fns/format';
import { storeItemForSignIn, setAuthModalState } from 'containers/Auth/actions';
import { makeSelectProfile } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import {
  getAlgoliaRatingQuery,
  getAlgoliaTopicsQuery,
  getTopicsTitleQuery,
  getArticlesAlgoliaQuery,
} from 'containers/Main/utils';
import { isBot } from 'utils/stringUtils';
import {
  GET_SAVED_RESOURCES,
  GET_ALGOLIA_RESOURCES,
  GET_ALGOLIA_TOPIC_FACETS,
  SAVE_RESOURCE,
  RATE_RESOURCE,
  REVIEW_RESOURCE,
  GET_RESOURCE_REVIEW,
  VOTE_HELPFUL_RESOURCE,
  GET_HELPFUL_RATINGS,
  GET_RECOMMENDED_RESOURCES,
  GET_USER_REVIEWS,
} from './constants';
import {
  processing,
  algoliaProcessing,
  algoliaNextPageProcessing,
  algoliaTopicsProcessing,
  getSavedResources,
  getSavedResourcesResult,
  saveResourceResult,
  rateResourceResult,
  getAlgoliaResourcesResult,
  getAlgoliaTopicFacetsResult,
  getAlgoliaResourcesFavoriteResult,
  getResourceReviewResult,
  reviewResourceResult,
  voteHelpfulResourceResult,
  getHelpfulRatings,
  getHelpfulRatingsResult,
  getRecommendedResourcesResult,
  getUserReviewsSuccess,
  fetchingUserReviews,
  setAudienceRelations,
} from './actions';
import {
  sortByTopicsRelevance,
  getFeedbackDBCollectionNameByType,
  getPopulateReview,
  getResourcesIndex,
} from './utils';
import { makeSelectAudienceRelations } from './selectors';

const {
  ALGOLIA: { RESOURCES_AVG_RATED_INDEX, RESOURCES_RELEVANCE_INDEX, NEWS_INDEX },
} = Config;

function* getSavedResourcesSaga() {
  try {
    yield put(processing(true));
    const userID = yield getFirebase().auth().currentUser.uid;
    const savedResourcesDocs = yield getFirebase()
      .firestore()
      .collection('user_resources')
      .doc(userID)
      .collection('saved_resources')
      .get();
    const savedResources = savedResourcesDocs.docs.map(doc => doc.data());

    yield put(getSavedResourcesResult(savedResources));
  } catch (error) {
    yield put(getSavedResourcesResult([]));
  } finally {
    yield put(processing(false));
  }
}

function* getAlgoliaResourcesSaga({ payload }) {
  const {
    itemIds,
    applyFilters = false,
    typeFilters,
    allTopicFilters,
    queryFilter = '',
    // userRatingFilter = '',
    // expertRatingFilter = '',
    costFilters,
    durationFilters,
    focusFilters,
    audienceFilters,
    availableInFilters,
    // geolocationFilters = '',
    presentation = 'mixed',
    withFilters,
    hitsPerPage = 12,
    page = 0,
    isFavorite = false,
  } = payload;
  try {
    if (page === 0) yield put(algoliaProcessing(true));
    else yield put(algoliaNextPageProcessing(true));

    let definitiveIndex =
      queryFilter !== ''
        ? RESOURCES_RELEVANCE_INDEX
        : RESOURCES_AVG_RATED_INDEX;
    if (definitiveIndex === RESOURCES_AVG_RATED_INDEX) {
      const clientInfo = yield select(makeSelectClientDetails());
      const resourcesWeightings = _get(
        clientInfo,
        'metadata.algoliaResourcesWeight',
      );

      definitiveIndex = getResourcesIndex(resourcesWeightings);
    }
    const AlgoliaIndex = AlgoliaClient.initIndex(definitiveIndex);
    const articlesQueryOnly = `AND ${getArticlesAlgoliaQuery()}`;
    const audienceRelations = yield select(makeSelectAudienceRelations());
    if (_isEmpty(audienceRelations)) {
      const audienceResponse = yield ContentfulClient.getEntries({
        content_type: 'audience',
        'fields.reviewStatus': 'Accepted',
      });
      audienceResponse.items.forEach(item => {
        audienceRelations[item.fields.name] = item;
      });
      yield put(setAudienceRelations(audienceRelations));
    }

    let idsQuery = '';
    if (!_isEmpty(itemIds)) {
      idsQuery += 'AND (';
      _forEach(itemIds, (value, index) => {
        idsQuery += `objectID:'${value}'`;
        if (index !== itemIds.length - 1) {
          idsQuery += ' OR ';
        }
      });
      idsQuery += ')';
    }

    let filterQuery = '';
    let focusQuery = '';
    let audienceQuery = '';
    let audienceOptionalQuery;
    let availableInQuery = '';
    let availableInOptionalQuery;
    if (applyFilters) {
      if (!_isEmpty(allTopicFilters)) {
        filterQuery += 'AND (';
        _forEach(allTopicFilters, (value, index) => {
          filterQuery += `allTopics.title:'${value}'`;
          if (index !== allTopicFilters.length - 1) {
            filterQuery += ' OR ';
          }
        });
        filterQuery += ')';
      }

      if (!_isEmpty(costFilters)) {
        filterQuery += 'AND (';
        _forEach(costFilters, (value, index) => {
          if (value === 'Free') {
            filterQuery += 'cost = 0 ';
          } else if (value === '$') {
            filterQuery += 'cost: 0.01 TO 24.99 ';
          } else if (value === '$$') {
            filterQuery += 'cost: 25 TO 99.99 ';
          } else if (value === '$$$') {
            filterQuery += 'cost > 100 ';
          }
          if (index !== costFilters.length - 1) {
            filterQuery += ' OR ';
          }
        });
        filterQuery += 'OR cost = -1) ';
      }

      if (!_isEmpty(durationFilters)) {
        filterQuery += 'AND (';
        _forEach(durationFilters, (value, index) => {
          if (value === '5-') {
            filterQuery += 'minutes < 5 ';
          } else if (value === '5-20') {
            filterQuery += 'minutes: 5 TO 20 ';
          } else if (value === '20+') {
            filterQuery += 'minutes > 20 ';
          }
          if (index !== durationFilters.length - 1) {
            filterQuery += ' OR ';
          }
        });
        filterQuery += 'OR minutes = -1) ';
      }

      if (!_isEmpty(focusFilters)) {
        focusQuery += 'AND (';
        _forEach(focusFilters, (value, index) => {
          focusQuery += `focus:"${value}"`;
          if (index !== focusFilters.length - 1) {
            focusQuery += ' OR ';
          }
        });
        focusQuery += ')';
      }

      if (!_isEmpty(availableInFilters)) {
        availableInQuery += 'AND (';
        _forEach(availableInFilters, (value, index) => {
          availableInQuery += `availableIn:"${value}"`;
          if (index !== availableInFilters.length - 1) {
            availableInQuery += ' OR ';
          }
        });
        availableInQuery += ')';
        // TODO: Update to allow multiple audiences
        // availableInOptionalQuery = availableInFilters
        //   .filter(value => value !== 'English')
        //   .map(value => `availableIn:"${value}"`);
        availableInOptionalQuery = `availableIn:${
          availableInFilters.filter(value => value !== 'English')[0]
        }`;
      }

      if (!_isEmpty(audienceFilters)) {
        // TODO: Update to allow multiple audiences
        audienceOptionalQuery = audienceFilters.map(
          value => `audience:${value}`,
        );

        if (
          _get(audienceRelations, [
            audienceFilters[0],
            'fields',
            'exclusive',
          ]) === 'Yes'
        ) {
          audienceQuery = ` AND audience:'${audienceFilters[0]}'`;
        } else {
          const excludedAudiences = _flatten(
            audienceFilters
              .map(item => audienceRelations[item].fields.exclude)
              .filter(item => item != null),
          )
            .filter(
              item =>
                item.fields.reviewStatus === 'Accepted' &&
                !audienceFilters.includes(item.fields.name),
            )
            .map(item => item.fields.name);

          if (!_isEmpty(excludedAudiences)) {
            audienceQuery = ' AND ';
            _forEach(excludedAudiences, (value, index) => {
              // TODO: Update to allow multiple audiences
              audienceQuery += `(audience:'${
                audienceFilters[0]
              }' OR NOT audience:'${value}')`;
              if (index !== excludedAudiences.length - 1) {
                audienceQuery += ' AND ';
              }
            });
          }
        }
      }
    }
    let optionalFilterQuery;
    if (audienceOptionalQuery || availableInOptionalQuery) {
      // if (audienceOptionalQuery && availableInOptionalQuery)
      //   optionalFilterQuery = [audienceOptionalQuery, availableInOptionalQuery]`;
      // else if (audienceOptionalQuery)
      //   optionalFilterQuery = audienceOptionalQuery;
      // else optionalFilterQuery = availableInOptionalQuery;
      if (audienceOptionalQuery) optionalFilterQuery = [audienceOptionalQuery];
      else optionalFilterQuery = [availableInOptionalQuery];
    }

    const hasTopics = typeFilters.includes('Topics');
    const hasAssessments = typeFilters.includes('Assessments');
    const filteredTypeFilters = typeFilters.filter(
      el => el !== 'Topics' && el !== 'Assessments',
    );
    let typeQuery = '';
    if (!_isEmpty(filteredTypeFilters)) {
      typeQuery = 'AND ';
      _forEach(filteredTypeFilters, (value, index) => {
        typeQuery += `type:'${value}'`;
        if (index !== filteredTypeFilters.length - 1) {
          typeQuery += ' OR ';
        }
      });
    }

    const algoliaRatingQuery = yield call(getAlgoliaRatingQuery);
    const algoliaTopicsQuery = yield call(
      getAlgoliaTopicsQuery,
      'allTopics.title',
    );
    const excludeTopicsTitles = yield call(getTopicsTitleQuery);

    let hits = [];
    let nbHits = 0;
    let nbPages = 0;
    let processingTimeMS = 0;
    const separateHits = {};

    // Prepare Topics filter query
    let topicsTitleQuery = '';
    if (!_isEmpty(allTopicFilters)) {
      topicsTitleQuery += 'AND (';
      _forEach(allTopicFilters, (value, index) => {
        topicsTitleQuery += `title:'${value}' OR allTopics.title:'${value}'`;
        if (index !== allTopicFilters.length - 1) {
          topicsTitleQuery += ' OR ';
        }
      });
      topicsTitleQuery += ')';
    }
    if (!_isEmpty(excludeTopicsTitles)) {
      topicsTitleQuery += 'AND ';
      _forEach(excludeTopicsTitles, (value, index) => {
        topicsTitleQuery += `NOT title:'${value}'`;
        if (index !== excludeTopicsTitles.length - 1) {
          topicsTitleQuery += ' AND ';
        }
      });
    }

    let topicsResponse = {
      nbHits: 0,
    };
    let assessmentsResponse = {
      nbHits: 0,
    };

    const queries = [
      {
        indexName: definitiveIndex,
        query: queryFilter,
        params: {
          hitsPerPage: 12,
          filters: `reviewStatus:'Accepted' AND type:'Topics' ${idsQuery} ${topicsTitleQuery} ${algoliaRatingQuery}`,
        },
      },
      {
        indexName: definitiveIndex,
        query: queryFilter,
        params: {
          hitsPerPage: 12,
          filters: `reviewStatus:'Accepted' AND type:'Assessments' ${algoliaTopicsQuery} ${algoliaRatingQuery} ${idsQuery} ${filterQuery}`,
        },
      },
    ];
    if (presentation === 'mixed') {
      const response = yield call(() =>
        AlgoliaClient.multipleQueries(
          queries.concat([
            {
              indexName: definitiveIndex,
              query: queryFilter,
              params: {
                page,
                hitsPerPage,
                getRankingInfo: false,
                filters: `reviewStatus:'Accepted' AND NOT type:'Topics' AND NOT type:'Assessments' ${typeQuery} ${idsQuery} ${focusQuery} ${filterQuery} ${algoliaRatingQuery} ${algoliaTopicsQuery} ${audienceQuery} ${availableInQuery} ${articlesQueryOnly}`,
                optionalFilters: optionalFilterQuery,
              },
            },
          ]),
        ),
      );

      let remainingResponse;
      [
        topicsResponse,
        assessmentsResponse,
        remainingResponse,
      ] = response.results;

      ({ hits, nbHits, nbPages, processingTimeMS } = remainingResponse);
    } else if (presentation === 'separate') {
      const response = yield call(() =>
        AlgoliaClient.multipleQueries(
          queries.concat(
            filteredTypeFilters.map(type => ({
              indexName: definitiveIndex,
              query: queryFilter,
              params: {
                removeWordsIfNoResults: 'none',
                hitsPerPage: 12,
                getRankingInfo: false,
                filters: `reviewStatus:'Accepted' AND NOT type:'Topics' AND NOT type:'Assessments' AND type:'${type}' ${idsQuery} ${focusQuery} ${filterQuery} ${algoliaRatingQuery} ${algoliaTopicsQuery} ${audienceQuery} ${availableInQuery} ${articlesQueryOnly}`,
                optionalFilters: optionalFilterQuery,
              },
            })),
          ),
        ),
      );

      let remainingResponses;
      [
        topicsResponse,
        assessmentsResponse,
        ...remainingResponses
      ] = response.results;
      remainingResponses.forEach((el, index) => {
        separateHits[filteredTypeFilters[index]] = el.hits;
      });

      nbHits = remainingResponses.reduce((acc, item) => acc + item.nbHits, 0);
    }

    // Get Topics info for Top Section
    const topics = topicsResponse.hits;

    // Get Assessments info for Top Section
    let assessments = [];

    if (hasAssessments && assessmentsResponse.hits) {
      assessments = assessmentsResponse.hits;
    }

    let finalNbHits = nbHits;
    if (
      hasTopics &&
      !(
        availableInFilters.length === 1 &&
        availableInFilters.includes('Spanish')
      )
    )
      finalNbHits += topicsResponse.nbHits;
    if (
      hasAssessments &&
      !(
        availableInFilters.length === 1 &&
        availableInFilters.includes('Spanish')
      )
    )
      finalNbHits += assessmentsResponse.nbHits;

    const data = {
      hits,
      separateHits,
      topics,
      assessments,
      nbHits: finalNbHits,
      generalNbHits: nbHits,
      nbPages,
      processingTimeMS,
    };
    if (!withFilters) {
      if (isFavorite) {
        yield put(
          getAlgoliaResourcesFavoriteResult({
            data,
          }),
        );
      } else {
        yield put(
          getAlgoliaResourcesResult({
            page,
            data,
          }),
        );
      }
    } else {
      const returnedTypeFilters = yield all([
        call(() =>
          AlgoliaIndex.searchForFacetValues('type', '', {
            maxFacetHits: 100,
            filters: `reviewStatus:'Accepted'`,
          }),
        ),
        call(() =>
          AlgoliaIndex.searchForFacetValues('type', '', {
            query: queryFilter,
            maxFacetHits: 100,
            filters: `reviewStatus:'Accepted' AND NOT type:'Topics' AND NOT type:'Assessments' ${idsQuery} ${focusQuery} ${filterQuery} ${algoliaTopicsQuery} ${algoliaRatingQuery} ${audienceQuery} ${availableInQuery}`,
          }),
        ),
      ]);
      const typeFilterOptions = returnedTypeFilters[0];
      const foundTypeFilters = returnedTypeFilters[1].facetHits.map(
        el => el.value,
      );
      typeFilterOptions.facetHits = returnedTypeFilters[0].facetHits.map(el => {
        if (el.value === 'Topics') {
          return {
            value: el.value,
            highlighted: el.highlighted,
            count:
              availableInFilters.length === 1 &&
              availableInFilters.includes('Spanish')
                ? 0
                : topicsResponse.nbHits,
          };
        }
        if (el.value === 'Assessments') {
          return {
            value: el.value,
            highlighted: el.highlighted,
            count:
              availableInFilters.length === 1 &&
              availableInFilters.includes('Spanish')
                ? 0
                : assessmentsResponse.nbHits,
          };
        }
        if (foundTypeFilters.includes(el.value)) {
          return {
            value: el.value,
            highlighted: el.highlighted,
            count: returnedTypeFilters[1].facetHits.find(
              filter => filter.value === el.value,
            ).count,
          };
        }

        return {
          value: el.value,
          highlighted: el.highlighted,
          count: 0,
        };
      });
      const allTopicsFilterOptions = yield call(() =>
        AlgoliaIndex.searchForFacetValues('allTopics.title', '', {
          maxFacetHits: 50,
          filters: `reviewStatus:'Accepted' ${idsQuery} ${algoliaTopicsQuery} ${algoliaRatingQuery}`,
        }),
      );

      // const returnedFocusFilters = yield all([
      //   call(() =>
      //     AlgoliaIndex.searchForFacetValues('focus', '', {
      //       maxFacetHits: 100,
      //       filters: `reviewStatus:'Accepted'`,
      //     }),
      //   ),
      //   call(() =>
      //     AlgoliaIndex.searchForFacetValues('focus', '', {
      //       maxFacetHits: 100,
      //       filters: `reviewStatus:'Accepted' ${typeQuery} ${idsQuery} ${filterQuery} ${algoliaTopicsQuery} ${algoliaRatingQuery} ${audienceQuery} ${availableInQuery}`,
      //     }),
      //   ),
      // ]);
      // const focusFilterOptions = returnedFocusFilters[0];
      // const foundFocusFilters = returnedFocusFilters[1].facetHits.map(
      //   el => el.value,
      // );
      // focusFilterOptions.facetHits = returnedFocusFilters[0].facetHits.map(
      //   el => {
      //     if (foundFocusFilters.includes(el.value)) {
      //       return {
      //         value: el.value,
      //         highlighted: el.highlighted,
      //         count: returnedFocusFilters[1].facetHits.filter(
      //           filter => filter.value === el.value,
      //         )[0].count,
      //       };
      //     }

      //     return {
      //       value: el.value,
      //       highlighted: el.highlighted,
      //       count: 0,
      //     };
      //   },
      // );

      const focusFilterOptions = yield call(() =>
        AlgoliaIndex.searchForFacetValues('focus', '', {
          maxFacetHits: 50,
          filters: `reviewStatus:'Accepted'`,
        }),
      );

      const audienceFilterOptions = yield call(() =>
        AlgoliaIndex.searchForFacetValues('audience', '', {
          maxFacetHits: 100,
          filters: `reviewStatus:'Accepted' ${typeQuery} ${idsQuery} ${focusQuery} ${filterQuery} ${algoliaTopicsQuery} ${algoliaRatingQuery} ${availableInQuery}`,
        }),
      );

      const availableFilterOptions = yield call(() =>
        AlgoliaIndex.searchForFacetValues('availableIn', '', {
          maxFacetHits: 50,
          filters: `reviewStatus:'Accepted' ${idsQuery} ${algoliaTopicsQuery} ${algoliaRatingQuery}`,
        }),
      );

      if (isFavorite) {
        yield put(
          getAlgoliaResourcesFavoriteResult({
            data,
            filters: {
              typeFilterOptions,
              allTopicsFilterOptions,
              focusFilterOptions,
              audienceFilterOptions,
              availableFilterOptions,
            },
          }),
        );
      } else {
        yield put(
          getAlgoliaResourcesResult({
            page,
            data,
            filters: {
              typeFilterOptions,
              allTopicsFilterOptions,
              focusFilterOptions,
              audienceFilterOptions,
              availableFilterOptions,
            },
          }),
        );
      }
    }
  } catch (error) {
    yield put(getAlgoliaResourcesResult({ error: true }));
  } finally {
    if (page === 0) yield put(algoliaProcessing(false));
    else yield put(algoliaNextPageProcessing(false));
  }
}

function* getTopicFacetsSaga({ payload }) {
  const { query } = payload;

  try {
    yield put(algoliaTopicsProcessing(true));

    const AlgoliaIndex = AlgoliaClient.initIndex(RESOURCES_AVG_RATED_INDEX);
    /**
     * Add rating filter cutoff if present on specific cobrand
     */
    const clientDetails = yield select(makeSelectClientDetails());

    /**
     * Exclude topic facets if present on specific cobrand
     */
    const clientExcludedTopic = _get(
      clientDetails,
      'excludeTopicCollection.items',
    );
    const excludedTopicTitle = !_isEmpty(clientExcludedTopic)
      ? clientExcludedTopic.map(topic => topic.title)
      : [];
    let excludeTopicQuery = '';
    if (!_isEmpty(excludedTopicTitle)) {
      _forEach(excludedTopicTitle, id => {
        excludeTopicQuery += ` AND NOT allTopics.title:'${id}'`;
      });
    }

    const allTopicsFilterOptions = yield call(() =>
      AlgoliaIndex.searchForFacetValues('allTopics.title', query, {
        maxFacetHits: 50,
        filters: `reviewStatus:'Accepted' ${excludeTopicQuery}`,
      }),
    );
    yield put(getAlgoliaTopicFacetsResult(allTopicsFilterOptions));
  } catch (error) {
    yield put(
      getAlgoliaTopicFacetsResult({
        facetHits: [],
      }),
    );
  } finally {
    yield put(algoliaTopicsProcessing(false));
  }
}

function* saveResourceSaga({ payload }) {
  try {
    yield put(processing(true));
    yield put(storeItemForSignIn({}));
    let userID;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (error) {
      // const {
      //   location: { pathname },
      // } = payload;
      yield put(storeItemForSignIn({ type: 'resource', payload }));
      yield put(saveResourceResult({ error: true }));
      yield put(setAuthModalState({ show: true, type: 'favorite' }));
      // yield put(push(`/login?redirect=${pathname}`));
    }
    if (userID != null) {
      const { save, resource } = payload;
      const userResourceDoc = yield getFirebase()
        .firestore()
        .collection('user_resources')
        .doc(userID)
        .collection('saved_resources')
        .doc(resource.resourceID);
      if (save) {
        if (!userResourceDoc.exists) {
          yield userResourceDoc.set(resource);
          yield put(
            saveResourceResult({
              added: true,
              error: false,
            }),
          );
        } else {
          yield put(
            saveResourceResult({
              added: false,
              error: false,
            }),
          );
        }
      } else {
        yield userResourceDoc.delete();
        yield put(
          saveResourceResult({
            added: false,
            error: false,
          }),
        );
      }
    }
  } catch (error) {
    yield put(saveResourceResult({ error: true }));
  } finally {
    yield put(processing(false));
    yield put(getSavedResources());
  }
}

// function* getResourceRatingSaga({ payload }) {
//   try {
//     yield put(processing(true));
//     const { itemId } = payload;
//     const userID = yield getFirebase().auth().currentUser.uid;
//     const userRatingDoc = yield getFirebase()
//       .firestore()
//       .collection('user_feedback_resources')
//       .doc(itemId)
//       .collection('reviews')
//       .doc(userID)
//       .get();
//     const rating = userRatingDoc.data();
//     yield put(getResourceRatingResult(rating));
//   } catch (error) {
//     yield put(getResourceRatingResult({}));
//   } finally {
//     yield put(processing(false));
//   }
// }

function* rateResourceSaga({ payload }) {
  try {
    yield put(processing(true));
    yield put(storeItemForSignIn({}));
    let userID;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (error) {
      // const {
      //   location: { pathname },
      // } = payload;
      yield put(storeItemForSignIn({ type: 'rate', payload }));
      yield put(
        rateResourceResult({
          rated: false,
          error: true,
        }),
      );
      yield put(setAuthModalState({ show: true, type: 'rate' }));
      // yield put(push(`/login?redirect=${pathname}`));
    }
    if (userID != null) {
      const shouldPopulateReview = getPopulateReview();

      const profile = yield select(makeSelectProfile());
      if (_isEmpty(profile.displayName))
        throw new Error('User name is missing');

      const { resourceID, data } = payload;

      // Create real object to allow query on Admin tool
      if (shouldPopulateReview) {
        const resourceRef = yield getFirebase()
          .firestore()
          .collection('user_feedback_resources')
          .doc(resourceID);
        const resourceDoc = yield resourceRef.get();
        if (!resourceDoc.exists) yield resourceRef.set({ id: resourceID });
      }

      const userReviewRef = yield getFirebase()
        .firestore()
        .collection('user_feedback_resources')
        .doc(resourceID)
        .collection('reviews')
        .doc(userID);
      const userReviewDoc = yield userReviewRef.get();
      if (!userReviewDoc.exists) data.visible = true;
      yield userReviewRef.set(
        {
          person: profile.displayName,
          person_img: profile.avatarUrl || null,
          ...data,
        },
        {
          merge: true,
        },
      );
      const userReviewSnapshot = yield userReviewRef.get();
      const fullReview = yield userReviewSnapshot.data();
      yield put(
        rateResourceResult({
          review: fullReview,
          rated: true,
          error: false,
        }),
      );
    }
  } catch (error) {
    yield put(rateResourceResult({ error: true }));
  } finally {
    yield put(processing(false));
  }
}

function* getResourceReviewSaga({ payload }) {
  try {
    yield put(processing(true));
    const { itemId } = payload;
    const userID = yield getFirebase().auth().currentUser.uid;
    const userReviewDoc = yield getFirebase()
      .firestore()
      .collection('user_feedback_resources')
      .doc(itemId)
      .collection('reviews')
      .doc(userID)
      .get();
    const review = userReviewDoc.data();
    yield put(getResourceReviewResult(review));
  } catch (error) {
    yield put(getResourceReviewResult({}));
  } finally {
    yield put(processing(false));
  }
}

function* reviewResourceSaga({ payload }) {
  try {
    yield put(processing(true));
    yield put(storeItemForSignIn({}));
    let userID;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (error) {
      // const {
      //   location: { pathname },
      // } = payload;
      yield put(storeItemForSignIn({ type: 'review', payload }));
      yield put(
        reviewResourceResult({
          error: true,
          errorMessage: 'Oh, something went wrong. Please, try again.',
        }),
      );
      // yield put(push(`/login?redirect=${pathname}`));
    }
    if (userID != null) {
      const shouldPopulateReview = getPopulateReview();

      const { resourceID, data } = payload;

      // Create real object to allow query on Admin tool
      if (shouldPopulateReview) {
        const resourceRef = yield getFirebase()
          .firestore()
          .collection('user_feedback_resources')
          .doc(resourceID);
        const resourceDoc = yield resourceRef.get();
        if (!resourceDoc.exists) yield resourceRef.set({ id: resourceID });
      }

      const userReviewDoc = yield getFirebase()
        .firestore()
        .collection('user_feedback_resources')
        .doc(resourceID)
        .collection('reviews')
        .doc(userID);
      yield userReviewDoc.set(data, { merge: true });

      const userReviewSnapshot = yield userReviewDoc.get();
      const fullReview = yield userReviewSnapshot.data();
      yield put(
        reviewResourceResult({
          review: fullReview,
          rated: true,
          error: false,
        }),
      );
    }
  } catch (error) {
    yield put(
      reviewResourceResult({ error: true, errorMessage: error.message }),
    );
  } finally {
    yield put(processing(false));
  }
}

function* voteHelpfulResourceSaga({ payload }) {
  const { resourceID, isThumbUp, data = {}, name, slug, type } = payload;
  const collectionName = getFeedbackDBCollectionNameByType(type);
  try {
    yield put(processing(true));

    let userID;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (e) {
      userID = yield getCookie('userIdTemp');
    }

    const brand = getCookie('brand') || 'none';

    const userFeedbackDoc = yield getFirebase()
      .firestore()
      .collection(collectionName)
      .doc(resourceID);
    yield getFirebase()
      .firestore()
      .runTransaction(transaction =>
        transaction.get(userFeedbackDoc).then(doc => {
          if (doc.exists) {
            const ratings = _clone(doc.data());

            ratings.name = name;
            ratings.type = type;

            const ratingsUpIds = ratings.up.users
              ? ratings.up.users.map(el => el.id)
              : [];
            const ratingsDownIds = ratings.down.users
              ? ratings.down.users.map(el => el.id)
              : [];
            const prevVoteUp = ratingsUpIds.includes(userID);
            const prevVoteDown = ratingsDownIds.includes(userID);

            if (isThumbUp) {
              if (!prevVoteUp) {
                if (ratings.up.users)
                  ratings.up.users.push({
                    id: userID,
                    brand,
                    timestamp: Date.now(),
                    ...data,
                  });
                else
                  ratings.up.users = [
                    { id: userID, brand, timestamp: Date.now(), ...data },
                  ];
                ratings.up.count += 1;
              } else {
                ratings.up.users = ratings.up.users.filter(
                  el => el.id !== userID,
                );
                ratings.up.users.push({
                  id: userID,
                  brand,
                  timestamp: Date.now(),
                  ...data,
                });
              }

              if (prevVoteDown) {
                ratings.down.users = ratings.down.users.filter(
                  el => el.id !== userID,
                );
                ratings.down.count -= 1;
              }
            } else {
              if (!prevVoteDown) {
                if (ratings.down.users)
                  ratings.down.users.push({
                    id: userID,
                    brand,
                    timestamp: Date.now(),
                    ...data,
                  });
                else
                  ratings.down.users = [
                    { id: userID, brand, timestamp: Date.now(), ...data },
                  ];
                ratings.down.count += 1;
              } else {
                ratings.down.users = ratings.down.users.filter(
                  el => el.id !== userID,
                );
                ratings.down.users.push({
                  id: userID,
                  brand,
                  timestamp: Date.now(),
                  ...data,
                });
              }

              if (prevVoteUp) {
                ratings.up.users = ratings.up.users.filter(
                  el => el.id !== userID,
                );
                ratings.up.count -= 1;
              }
            }
            transaction.set(userFeedbackDoc, ratings);
          } else {
            const users = [
              { id: userID, brand, timestamp: Date.now(), ...data },
            ];
            const feedbackData = {
              name,
              type,
              up: {
                count: isThumbUp ? 1 : 0,
                users: isThumbUp ? users : [],
              },
              down: {
                count: !isThumbUp ? 1 : 0,
                users: !isThumbUp ? users : [],
              },
            };

            transaction.set(userFeedbackDoc, feedbackData);
          }
        }),
      );
    let pageName = 'Assessment Result';
    if (type === 'blog') pageName = 'Blog Page';
    else if (type === 'topic') pageName = 'Topic Page';
    Mixpanel.track(`${pageName} - Was this helpful`, {
      answer: isThumbUp,
      name,
      slug,
      secondaryOption: data.secondaryValue,
    });
    yield put(voteHelpfulResourceResult({ error: false }));
  } catch (error) {
    yield put(voteHelpfulResourceResult({ error: true }));
    yield put(processing(false));
  } finally {
    yield put(getHelpfulRatings({ resourceID, type }));
  }
}

function* getHelpfulRatingsSaga({ payload }) {
  try {
    yield put(processing(true));
    const { resourceID, type = '' } = payload;

    let userID;
    const collectionName = getFeedbackDBCollectionNameByType(type);
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (e) {
      userID = yield getCookie('userIdTemp');
    }
    const userFeedbackDoc = yield getFirebase()
      .firestore()
      .collection(collectionName)
      .doc(resourceID)
      .get();
    if (userFeedbackDoc.exists) {
      const ratings = userFeedbackDoc.data();
      const ratingsUpIds = ratings.up.users
        ? ratings.up.users.map(el => el.id)
        : [];
      const ratingsDownIds = ratings.down.users
        ? ratings.down.users.map(el => el.id)
        : [];
      const prevVoteUp = ratingsUpIds.includes(userID);
      const prevVoteDown = ratingsDownIds.includes(userID);

      let prevRating;
      if (prevVoteUp) prevRating = true;
      if (prevVoteDown) prevRating = false;
      yield put(
        getHelpfulRatingsResult({
          error: false,
          up: ratings.up.count,
          down: ratings.down.count,
          prevRating,
        }),
      );
    } else {
      yield put(
        getHelpfulRatingsResult({
          error: false,
          up: 0,
          down: 0,
          prevRating: null,
        }),
      );
    }
  } catch (error) {
    yield put(getHelpfulRatingsResult({ error: true }));
  } finally {
    yield put(processing(false));
  }
}

function* getRecommendedResources({ payload }) {
  try {
    if (isBot()) throw new Error('Do not call Algolia for Bot requests');

    const { topics, limit = 27, audience, type, slug } = payload;

    const clientInfo = yield select(makeSelectClientDetails());
    const resourcesWeightings = _get(
      clientInfo,
      'metadata.algoliaResourcesWeight',
    );

    const resourcesIndexName = getResourcesIndex(resourcesWeightings);

    const clientTopics = yield call(getTopicsTitleQuery);
    const algoliaRatingQuery = yield call(getAlgoliaRatingQuery);
    const algoliaTopicsQuery = yield call(
      getAlgoliaTopicsQuery,
      'allTopics.title',
    );
    const algoliaTopicsNewsQuery = yield call(getAlgoliaTopicsQuery, 'topics');

    const definitiveTopics = topics.filter(el => !clientTopics.includes(el));

    const audienceRelations = yield select(makeSelectAudienceRelations());
    if (_isEmpty(audienceRelations)) {
      const audienceResponse = yield ContentfulClient.getEntries({
        content_type: 'audience',
        'fields.reviewStatus': 'Accepted',
      });
      audienceResponse.items.forEach(item => {
        audienceRelations[item.fields.name] = item;
      });
      yield put(setAudienceRelations(audienceRelations));
    }

    let filterQuery = '';
    let newsFilterQuery = '';
    if (!_isEmpty(definitiveTopics)) {
      filterQuery = ' AND ';
      _forEach(definitiveTopics, (value, index) => {
        filterQuery += `allTopics.title:'${value}'`;
        newsFilterQuery += `topics:'${value}'`;
        if (index !== definitiveTopics.length - 1) {
          filterQuery += ' OR ';
          newsFilterQuery += ' OR ';
        }
      });
    }
    // TODO: Expand optionalFilterQuery when multiple filters are available
    let audienceQuery = '';
    let audienceOptionalQuery;
    if (!_isEmpty(audience)) {
      // TODO: Update to allow multiple audiences
      audienceOptionalQuery = audience
        .map(value => `audience:${value}`)
        .slice(0, 1);

      const excludedAudiences = _flatten(
        audience
          .map(item => audienceRelations[item].fields.exclude)
          .filter(item => item != null),
      )
        .filter(
          item =>
            item.fields.reviewStatus === 'Accepted' &&
            !audience.includes(item.fields.name),
        )
        .map(item => item.fields.name);

      if (!_isEmpty(excludedAudiences)) {
        audienceQuery = ' AND ';
        _forEach(excludedAudiences, (value, index) => {
          // TODO: Update to allow multiple audiences
          audienceQuery += `(audience:'${
            audience[0]
          }' OR NOT audience:'${value}')`;
          if (index !== excludedAudiences.length - 1) {
            audienceQuery += ' AND ';
          }
        });
      }
    }

    const response = yield call(() =>
      AlgoliaClient.multipleQueries([
        {
          indexName: resourcesIndexName,
          query: '',
          params: {
            hitsPerPage: limit,
            getRankingInfo: false,
            filters: `reviewStatus:'Accepted' AND type:'Assessments' ${filterQuery} ${algoliaRatingQuery} ${algoliaTopicsQuery} ${audienceQuery}`,
            optionalFilters: audienceOptionalQuery,
          },
        },
        {
          indexName: resourcesIndexName,
          query: '',
          params: {
            hitsPerPage: limit,
            getRankingInfo: false,
            filters: `reviewStatus:'Accepted' AND slug:'whats-your-mental-health-profile'`,
          },
        },
        {
          indexName: resourcesIndexName,
          query: '',
          params: {
            hitsPerPage: limit,
            getRankingInfo: false,
            filters: `reviewStatus:'Accepted' AND type:'${type}' AND NOT slug:"${slug}" ${filterQuery} ${algoliaRatingQuery} ${algoliaTopicsQuery} ${audienceQuery}`,
            optionalFilters: audienceOptionalQuery,
          },
        },
        {
          indexName: NEWS_INDEX,
          query: '',
          params: {
            hitsPerPage: limit,
            getRankingInfo: false,
            filters: `NOT slug:"${slug}" ${
              newsFilterQuery ? `AND ${newsFilterQuery}` : ''
            } ${algoliaTopicsNewsQuery}`,
            optionalFilters: audienceOptionalQuery,
          },
        },
      ]),
    );
    const sortedResources = sortByTopicsRelevance(
      _get(response, 'results.2.hits', []),
      topics,
    );
    const hits = [];
    if (_get(response, 'results.0.hits', []).length > 0) {
      hits.push(..._get(response, 'results.0.hits', []));
      hits.push(
        ...sortedResources.slice(
          0,
          9 - _get(response, 'results.0.hits', []).length,
        ),
      );
    } else {
      hits.push(..._get(response, 'results.1.hits'));
      if (hits.length > 0) hits.push(...sortedResources.slice(0, 8));
      else hits.push(...sortedResources.slice(0, 9));
    }
    yield put(
      getRecommendedResourcesResult({
        hits,
        news: _get(response, 'results.3.hits', []).map(el => ({
          ...el,
          type: 'News',
        })),
      }),
    );
  } catch (error) {
    yield put(
      getRecommendedResourcesResult({
        hits: [],
        news: [],
      }),
    );
  }
}

function getAndAssembleData(review) {
  const dateObj = new Date(review.datetime);
  const data = {
    date: format(new Date(dateObj), 'MMM DD, YYYY'),
    body: review.review,
    person: review.person,
    person_img: review.person_img,
    type: 'cm',
    rating: review.rating,
  };
  return data;
}

function* getUserReviewsSaga({ payload }) {
  try {
    yield put(fetchingUserReviews(true));
    const { itemId } = payload;
    let userID;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
    } catch (e) {
      userID = yield getCookie('userIdTemp');
    }
    const userFeedbacksDoc = yield getFirebase()
      .firestore()
      .collection('user_feedback_resources')
      .doc(itemId);
    const userReviewsSnapshot = yield userFeedbacksDoc
      .collection('reviews')
      // TODO: filter only visible reviews
      .get();
    let userReviews = {};
    userReviewsSnapshot.forEach(review => {
      userReviews = { ...userReviews, [review.id]: review.data() };
    });
    const finalUserReviews = userReviews
      ? Object.keys(userReviews).map(
          key =>
            userID !== key &&
            userReviews[key].visible !== false &&
            getAndAssembleData(userReviews[key]),
        )
      : [];
    yield put(getUserReviewsSuccess(finalUserReviews.filter(Boolean)));
  } finally {
    yield put(fetchingUserReviews(false));
  }
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(GET_SAVED_RESOURCES, getSavedResourcesSaga);
  yield takeLatest(GET_ALGOLIA_RESOURCES, getAlgoliaResourcesSaga);
  yield takeLatest(GET_ALGOLIA_TOPIC_FACETS, getTopicFacetsSaga);
  yield takeLatest(GET_RESOURCE_REVIEW, getResourceReviewSaga);
  yield takeLatest(SAVE_RESOURCE, saveResourceSaga);
  yield takeLatest(RATE_RESOURCE, rateResourceSaga);
  yield takeLatest(REVIEW_RESOURCE, reviewResourceSaga);
  yield takeLatest(VOTE_HELPFUL_RESOURCE, voteHelpfulResourceSaga);
  yield takeLatest(GET_HELPFUL_RATINGS, getHelpfulRatingsSaga);
  yield takeLatest(GET_RECOMMENDED_RESOURCES, getRecommendedResources);
  yield takeLatest(GET_USER_REVIEWS, getUserReviewsSaga);
}
